import { atom } from 'jotai';
import type { Community } from '@/types/db';
import type { SubdomainType } from '@/lib/utils/subdomain';

/**
 * AI Context: Community Atoms (Jotai)
 *
 * Purpose: Global state management for the current application context
 *          (platform vs community) and the active community's data.
 * Location: @/lib/stores/community.ts
 * Constraints: Used by AppContextProvider and various components needing
 *              contextual data. Initialize appropriately.
 */

// ============================================================================
// Application Context Atoms
// ============================================================================

/**
 * Stores the overall type of the application context ('app', 'platform', 'community').
 * Starts as 'loading' until determined by AppContextProvider.
 */
export const appContextTypeAtom = atom<SubdomainType | 'loading'>('loading');

/**
 * Stores the identifier associated with the context (e.g., community slug, 'platform', or custom hostname).
 * Null if context is 'app' or undetermined.
 */
export const appSubdomainIdentifierAtom = atom<string | null>(null);

/**
 * Stores whether the current identifier is a custom domain.
 */
export const isCustomDomainAtom = atom<boolean>(false);

// ============================================================================
// Current Community State Atoms
// ============================================================================

/**
 * Stores the fully loaded data for the current community, if context type is 'community'.
 */
export const currentCommunityAtom = atom<Community | null>(null);

/**
 * Indicates whether the community data is currently being loaded.
 * Start as false to prevent cascade loading on tab focus.
 */
export const communityLoadingAtom = atom<boolean>(false);

/**
 * Stores any error message encountered while loading community data.
 */
export const communityErrorAtom = atom<string | null>(null);

// Combined community state atom - optimized for performance
export const communityStateAtom = atom((get) => ({
  community: get(currentCommunityAtom),
  isLoading: get(communityLoadingAtom),
  error: get(communityErrorAtom),
}));
